import type { UserAutocompleteOptionProps } from '../UsersAutocomplete.types.ts';

import { Box, Typography } from '@mui/material';

import { temporaryColors } from '#lib/mui.ts';
import { getFullNameWithPronouns } from '#utils/user.ts';
import * as utils from '../UsersAutocomplete.utils.ts';

export const ComprehensiveOption = <
  IncludeGroups extends boolean | undefined,
  EnableCustomEmail extends boolean | undefined,
  EnableCustomPhoneNumber extends boolean | undefined,
>({
  props,
  option,
  state,
}: UserAutocompleteOptionProps<IncludeGroups, EnableCustomEmail, EnableCustomPhoneNumber>) => {
  // @ts-expect-error - TS complains there's no key property in the props but
  // that's not the case, its value corresponds to the return type of
  // `UsersAutocomplete.utils.ts@getOptionKey` as provided in the
  // `getOptionKey` prop of the autocomplete in `UsersAutocomplete.tsx`. This
  // is required because otherwise the key would be spread into the props
  // of the `li` element, logging an error in the console.
  const { key, ...propsWithoutKey } = props;
  const typedKey = key as ReturnType<typeof utils.getOptionKey>;

  if (utils.isTemporaryUser(option)) {
    return (
      <li key={typedKey} {...propsWithoutKey}>
        {option.label}
      </li>
    );
  }

  return (
    <Box
      key={typedKey}
      {...propsWithoutKey}
      component="li"
      sx={{
        backgroundColor: state.selected ? utils.selectedBgColor : undefined,
        '&:not(:last-child)': {
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Box width="100%">
        {utils.isExistingUser(option) ? (
          <>
            <Typography component="div" variant="body2">
              {getFullNameWithPronouns(option)}
            </Typography>
            {utils.shouldDisplayRoleDetails(option.role) && (
              <Typography component="div" variant="caption">
                <Typography
                  component="span"
                  variant="inherit"
                  fontWeight={700}
                  color={utils.getRoleColor(option.role)}
                >
                  {option.role}
                </Typography>
                {utils.shouldDisplayRoleExtraDetails(option.role) &&
                  utils.getRoleExtraDetails(option)}
              </Typography>
            )}
            {utils.shouldDisplayEmail(option) && (
              <Typography component="div" variant="caption">
                {utils.getEmail(option)}
              </Typography>
            )}
          </>
        ) : (
          <>
            <Typography component="div" variant="body2">
              {option.name}
            </Typography>
            <Typography
              component="div"
              variant="caption"
              fontWeight={700}
              color={temporaryColors.blue}
            >
              Group
            </Typography>
            <Typography
              component="div"
              variant="caption"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              {utils.getUsersInGroup(option)}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};
